import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Table,
    TableRow,
    TableCell,
    TableBody,
    TableHead,
    IconButton,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { EditOutlined, DeleteOutline } from "@mui/icons-material";
import { collection, getDocs, doc, setDoc, deleteDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../../firebase"; 

const PAGE_SIZE = 5;

function ProdVehicles() {
    const [vehicles, setVehicles] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [openDialog, setOpenDialog] = useState(false);

    // Form state matching exactly what needs to be saved
    const [form, setForm] = useState({
        vehicleType: "",
        vehicleNo: "",
        volume: "",
    });

    // ================= Load Vehicles =================
    useEffect(() => {
        loadVehicles();
    }, []);

    const loadVehicles = async () => {
        try {
            // Using correct collection name: prodvehicles
            const snap = await getDocs(collection(db, "prodvehicles"));
            const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setVehicles(arr);
        } catch (error) {
            console.error("Error fetching vehicles:", error);
        }
    };

    // ================= Form Change Handler =================
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // ================= Close Dialog & Reset Form =================
    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingId(null);
        setForm({ vehicleType: "", vehicleNo: "", volume: "" });
    };

    // ================= Save / Update =================
    const saveVehicle = async () => {
        const { vehicleType, vehicleNo, volume } = form;
        
        if (!vehicleType || !vehicleNo || !volume) {
            Swal.fire("Error", "Please fill all required fields", "error");
            return;
        }

        try {
            const id = editingId || doc(collection(db, "prodvehicles")).id;
            await setDoc(doc(db, "prodvehicles", id), {
                ...form,
                createdTime: editingId ? (form.createdTime || Timestamp.now()) : Timestamp.now(),
            });

            Swal.fire({
                title: "Success",
                text: editingId ? "Vehicle updated successfully" : "Vehicle added successfully",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });

            handleCloseDialog();
            loadVehicles();
        } catch (error) {
            Swal.fire("Error", error.message, "error");
        }
    };

    // ================= Edit Click Handler =================
    const editVehicle = (vehicle) => {
        setForm({
            vehicleType: vehicle.vehicleType || "",
            vehicleNo: vehicle.vehicleNo || "",
            volume: vehicle.volume || "",
            ...(vehicle.createdTime && { createdTime: vehicle.createdTime }) // Persist timestamp if exists
        });
        setEditingId(vehicle.id);
        setOpenDialog(true);
    };

    // ================= Delete Handler =================
    const deleteVehicle = async (id) => {
        const res = await Swal.fire({
            title: "Delete Vehicle?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel"
        });

        if (!res.isConfirmed) return;

        try {
            await deleteDoc(doc(db, "prodvehicles", id));
            Swal.fire({
                title: "Deleted",
                text: "Vehicle has been removed.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });
            loadVehicles();
        } catch (error) {
            Swal.fire("Error", error.message, "error");
        }
    };

    // ================= Filter & Pagination =================
    const filtered = vehicles.filter(
        (v) =>
            v.vehicleNo?.toLowerCase().includes(search.toLowerCase()) ||
            v.vehicleType?.toLowerCase().includes(search.toLowerCase())
    );
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box minHeight="100vh" bgcolor="#f4f6f8">
            <Box flex={1} p={3}>
                
                {/* Header Section */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight={800}>
                        Production Vehicals
                    </Typography>
                    <Button variant="contained" onClick={() => setOpenDialog(true)}>
                        Add New Vehicle
                    </Button>
                </Box>

                {/* ================= Search ================= */}
                <TextField
                    label="Search by Vehicle No or Type"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1); // Reset to page 1 during searches
                    }}
                    sx={{ mb: 3, width: "100%", bgcolor: "#fff" }}
                    size="small"
                />

                {/* ================= Data Table ================= */}
                <Card elevation={2}>
                    <CardContent>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sr No</TableCell>
                                    <TableCell>Vehicle Type</TableCell>
                                    <TableCell>Vehicle No</TableCell>
                                    <TableCell>Volume</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginated.length > 0 ? (
                                    paginated.map((vehicle, index) => (
                                        <TableRow key={vehicle.id} hover>
                                            {/* Auto-generating sequential serial number dynamically */}
                                            <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                                            <TableCell>{vehicle.vehicleType}</TableCell>
                                            <TableCell>{vehicle.vehicleNo}</TableCell>
                                            <TableCell>{vehicle.volume}</TableCell>
                                            <TableCell align="center">
                                                <IconButton color="primary" onClick={() => editVehicle(vehicle)}>
                                                    <EditOutlined />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => deleteVehicle(vehicle.id)}>
                                                    <DeleteOutline />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" style={{ padding: "20px" }}>
                                            No vehicles found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {/* Pagination Component */}
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Pagination
                                count={Math.ceil(filtered.length / PAGE_SIZE)}
                                page={page}
                                onChange={(e, v) => setPage(v)}
                                color="primary"
                            />
                        </Box>
                    </CardContent>
                </Card>

                {/* ================= Add / Edit Dialog Window ================= */}
                <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                    <DialogTitle>{editingId ? "Edit Vehicle Info" : "Add New Vehicle"}</DialogTitle>
                    <DialogContent>
                        <Box mt={1} display="flex" flexDirection="column" gap={2.5}>
                            <TextField
                                label="Vehicle Type"
                                name="vehicleType"
                                value={form.vehicleType}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                            />
                            <TextField
                                label="Vehicle No"
                                name="vehicleNo"
                                value={form.vehicleNo}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                            />
                            <TextField
                                label="Volume"
                                name="volume"
                                value={form.volume}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5 }}>
                        <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                        <Button variant="contained" onClick={saveVehicle}>
                            {editingId ? "Update" : "Add"}
                        </Button>
                    </DialogActions>
                </Dialog>

            </Box>
        </Box>
    );
}

export default ProdVehicles;